import React, { useEffect, useMemo, useRef, useState } from 'react';
import HeaderNavigationDesktop from 'components/commercetools-ui/organisms/header/header-navigation/header-navigation-desktop';
import { CategoryBar, CategoryBarData } from 'shared/types/product/Category';
import { TasticProps } from 'frontastic/tastics/types';
import { useCategoryBar } from 'providers/categoryBar';
import { CategoryDropdown } from './categoryDropdown';
import { desktop } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import Link from 'components/commercetools-ui/atoms/link';
import { useSearchParams } from 'next/navigation';
import { Swiper, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import { filterVisibleCategories } from 'components/commercetools-ui/organisms/header/header-navigation/helpers';
import {  useProduct } from 'frontastic';


import { useFeatures } from 'providers/feature-flag';

const EMPTY_ARRAY: CategoryBarData[] = [];

const CategoryBarTastic = ({ data }: TasticProps<CategoryBar>) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { enableScheduleCategoryAnnouncement , enableDealsPage} = useFeatures();
  const isScheduleCategoryAnnouncementEnabled = enableScheduleCategoryAnnouncement();
  const isDealsPageEnabled = enableDealsPage();
  const category = useMemo(() => {
    if (isScheduleCategoryAnnouncementEnabled) {
      return filterVisibleCategories(data.category ?? EMPTY_ARRAY);
    }
    return data.category ?? EMPTY_ARRAY;
  }, [isScheduleCategoryAnnouncementEnabled, data.category]);

  const backButtonText = data.backButtonText.trim();
  const { setCategoryBarData, announcementBarData } = useCategoryBar();
  const [activeCategory, setActiveCategory] = useState<CategoryBarData | null>(null);
  const [activeCategoryButton, setActiveCategoryButton] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [animateDropdownPosition, setAnimateDropdownPosition] = useState(false);
  const mobileCategoryContainerRef = useRef<HTMLDivElement>(null);
  const animatePositionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { queryProductDiscounts } = useProduct();
  const [discountProductCount, setDiscountProductCount] = useState<any>(null);
  const[isFetchedDiscounts, setIsFetchedDiscounts] = useState(false)

  const fetchDiscountsProducts = async () => {
    try {
      const res = await queryProductDiscounts();
      if (res.isError) {
        console.error('Error fetching discounts:', res.isError);
      } else {
        setDiscountProductCount(res?.data);
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
    }finally {
      setIsFetchedDiscounts(true);
    }
  };

  useEffect(() => {
    if (category && category.length > 0) {
      setCategoryBarData(category);
      void fetchDiscountsProducts();
    }
  }, [category, setCategoryBarData]);

  const searchParams = useSearchParams();

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const closeDropdown = () => {
    if (!activeCategory) return;

    setAnimateDropdownPosition(false);
    setDropdownOpen(false);
    clearCloseTimeout();

    // we want to give the dropdown time to animate closed before we clear the active category
    closeTimeoutRef.current = setTimeout(() => {
      setActiveCategory(null);
      setActiveCategoryButton(null);
      closeTimeoutRef.current = null;
    }, 300);
  };

  useEffect(() => {
    clearCloseTimeout();
    setActiveCategory(null);
    setActiveCategoryButton(null);
    setDropdownOpen(false);
    setAnimateDropdownPosition(false);
  }, [searchParams.toString()]);

  // Close the dropdown if the user clicks outside of the category bar or dropdown
  useEffect(() => {
    if (!activeCategory) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileCategoryContainerRef.current &&
        !mobileCategoryContainerRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeCategory]);

  // Unmount effect to clear timeouts
  useEffect(() => {
    return () => {
      if (animatePositionTimeoutRef.current) {
        clearTimeout(animatePositionTimeoutRef.current);
        animatePositionTimeoutRef.current = null;
      }
      clearCloseTimeout();
    };
  }, []);

  const onCategoryButtonClick = (selectedCategoryButton: HTMLButtonElement, clickedCategoryData: CategoryBarData) => {
    const isSameCategory = activeCategory?.categoryName === clickedCategoryData?.categoryName;
    const nextCategory = isSameCategory ? null : clickedCategoryData;

    // Prevent the dropdown from closing when we switch categories
    clearCloseTimeout();

    if (!nextCategory) {
      closeDropdown();
      return;
    }

    if (activeCategory) {
      // If we're switching categories, tell the dropdown to animate the 
      // transition of the dropdown to underneath the new active category button
      setAnimateDropdownPosition(true);
      if (animatePositionTimeoutRef.current) {
        clearTimeout(animatePositionTimeoutRef.current);
      }
      animatePositionTimeoutRef.current = setTimeout(() => {
        setAnimateDropdownPosition(false);
        animatePositionTimeoutRef.current = null;
      }, 315);
    }

    setActiveCategory(nextCategory);
    setActiveCategoryButton(nextCategory ? selectedCategoryButton : null);
    requestAnimationFrame(() => setDropdownOpen(true));
  };

  const updateCategories = <T extends { categoryLinks?: { link?: string } }>(data: T[], total: number): T[] => {
    if (total > 0) {
      return data;
    }
    return data.filter((item) => item?.categoryLinks?.link?.toLowerCase() !== '/deals');
  };

  const updatedData = updateCategories(category, discountProductCount?.total);

  let categoryData

  if (isDealsPageEnabled) {
    categoryData = updatedData;
  } else {
    categoryData = category
  }

  return (
    <>
      {!isDesktopSize && (
        <div ref={mobileCategoryContainerRef} className="fixed top-[66px] z-30 pb-12 pt-16 flex w-full items-center justify-start bg-white dark:bg-gmsb-header-gray">
          <div className="w-full" data-ffid="ctr-cat-menu-options-mobile">
            <Swiper
              slidesPerView="auto"
              spaceBetween={8}
              slidesOffsetBefore={20}
              allowTouchMove
              simulateTouch
              className="w-full"
            >
              {categoryData?.map((cat: CategoryBarData) => {
                return (
                  <SwiperSlide key={cat.categoryName} className="!w-fit">
                    {cat?.subcategoryOptions && cat?.subcategoryOptions?.length > 0 ? (
                      <button
                        type="button"
                        onClick={(event) => onCategoryButtonClick(event.currentTarget, cat)}
                          className={`inline-block whitespace-nowrap rounded-lg px-10 py-12 text-sm ${activeCategory?.categoryName === cat?.categoryName
                            ? 'bg-green-600 text-white'
                            : 'bg-[#F5F5F5] text-[#5C5C5C]'
                        }`}
                        data-ffid={`btn-cat-menu-${cat?.categoryNameFFID}`}
                      >
                        {cat?.categoryName}
                      </button>
                    ) : (
                      <Link
                        link={cat?.categoryLinks}
                        onClick={closeDropdown}
                          className={`inline-block whitespace-nowrap rounded-lg px-10 py-12 text-sm ${activeCategory?.categoryName === cat?.categoryName
                            ? 'bg-green-600 text-white'
                            : 'bg-[#F5F5F5] text-[#5C5C5C]'
                        }`}
                        data-ffid={`lnk-cat-menu-${cat?.categoryNameFFID }-link`}
                      >
                        {cat?.categoryName}
                      </Link>
                    )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          {activeCategory && (
            <CategoryDropdown
              category={activeCategory}
              anchorEl={activeCategoryButton}
              open={dropdownOpen}
              animatePosition={animateDropdownPosition}
              backButtonText={backButtonText}
              onLinkClick={closeDropdown}
            />
          )}
        </div>
      )}
      {isDesktopSize && isFetchedDiscounts && (
        <HeaderNavigationDesktop
          links={categoryData?.map((category: CategoryBarData) => category)}
          announcementBarData={announcementBarData}
        />
      )}
    </>
  );
};

export default CategoryBarTastic;
